import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const StatusTypeModal = ({ isOpen, onClose, onSave, statusType }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (isOpen) {
      if (statusType) {
        setFormData({ ...statusType, color: statusType.color || '#38bdf8' });
      } else {
        setFormData({
          name: '',
          type: 'meta',
          color: '#38bdf8',
        });
      }
    }
  }, [statusType, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!formData.name) throw new Error("Nome é obrigatório");
      if (!formData.type) throw new Error("Tipo é obrigatório");

      const dataToSend = { ...formData, empresa_id: currentUser.empresa_id };
      await onSave(dataToSend);
      onClose();
    } catch (error) {
      toast({
        title: "Erro de Validação",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-white/20 text-white">
        <DialogHeader>
          <DialogTitle>
            {statusType ? 'Editar Tipo de Status' : 'Novo Tipo de Status'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {statusType ? 'Modifique o tipo de status personalizado.' : 'Crie um novo tipo de status personalizado.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Nome</label>
            <input
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="Ex: Em Andamento"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Tipo</label>
            <select
              name="type"
              value={formData.type || 'meta'}
              onChange={handleChange}
              className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="meta">Meta</option>
              <option value="servico">Serviço</option>
              <option value="atendimento">Atendimento</option>
              <option value="implementacao">Implementação</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Cor</label>
            <input
              type="color"
              name="color"
              value={formData.color || '#38bdf8'}
              onChange={handleChange}
              className="w-12 h-12 p-0 border-none bg-transparent cursor-pointer"
              style={{ background: 'none' }}
            />
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose} className="hover:bg-white/10 text-white">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StatusTypeModal;